import { useQuery } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios, { AxiosError } from 'axios'

import type { HeartbeatSnapshot } from 'api/aws/heartbeat/useOrgHeartbeatSnapshot'

/**
 * Dry-run preview of what `submitDealToHeartbeat` would push for the
 * given deal. Mirrors the shape returned by the backend's
 * `GET /aws/heartbeat/deal/:PK/:SK/preview`.
 *
 * No writes — safe to call as often as the UI wants.
 */

export interface TeamMemberPreview {
  user_uuid: string
  email: string | null
  name: string | null
  role: string
  heartbeat_user_id: number | null
  mirrored: boolean
}

export interface AccountPreview {
  /** `not_linked` blocks the push (skipReason='org_not_linked'). Account
   *  creation from Grobotic is disabled — the link must be resolved on
   *  MOTHERSHIP before the deal can push. */
  action: 'not_linked' | 'already_linked'
  name: string
  type: 'prospect'
  heartbeat_account_id: number | null
  team_members: TeamMemberPreview[]
}

export interface ParentAccountPreview {
  action: 'not_linked' | 'already_linked' | 'none'
  org_pk: string | null
  name: string | null
  heartbeat_account_id: number | null
}

export interface SubmitterPreview {
  user_uuid: string | null
  email: string | null
  name: string | null
  heartbeat_user_id: number | null
  mirrored: boolean
}

export interface FlowAContractPreview {
  start_date: string
  end_date?: string
  service_lines: string[]
  subscriptions: Array<{
    name: string
    service_line_code: string
    portion: 'spend' | 'fee' | 'full'
    recurring_amount: number
    total: number
    start_date?: string
    end_date?: string
    is_mrr: boolean
    is_service_mrr: boolean
    source_product_sk: string
  }>
  totals: { monthly: number; contract: number }
  /** True when the deal has at least one product that activates later
   *  than the deal's start date. UI shows a "Flighted Schedule" banner. */
  is_flighted: boolean
  /** Per-product future-activation entries — get pushed as service-change
   *  submissions instead of being included on the new contract. */
  flighted_activations: Array<{
    /** Products live on the line at the new level, comma-joined. */
    product_name: string
    service_line: string
    change_type: 'Increase' | 'Decrease' | 'Cancel'
    /** Line MRR the day before the step. */
    current_mrr: number
    /** Line MRR from the effective date onward. */
    recurring_amount: number
    effective_date: string
    length_of_change_months: number
    description: string
  }>
}

export interface FlowBChangePreview {
  change_type: 'Increase' | 'Decrease' | 'Cancel'
  service_line: string
  /** When this step takes effect — a version can emit several dated steps on
   *  one service line, so the date is what separates two rows for that line. */
  effective_date: string
  length_of_change_months: number
  current_mrr: number
  new_mrr: number
  delta_mrr: number
  current_products: string[]
  new_products: string[]
}

export type PredecessorSource =
  | 'predecessorId'
  | 'fallback-master-agreement'
  | 'fallback-same-type'
  | 'fallback-latest-active'

export interface FlowBPreview {
  predecessor: {
    PK: string
    SK: string
    name: string | null
    type: string | null
    source: PredecessorSource
  }
  changes: FlowBChangePreview[]
  /** Service lines where net MRR is unchanged but product composition
   *  shifted between versions. Heartbeat's submission model is
   *  service-line-aggregated, so these don't emit a submission — ops
   *  needs to manually reconcile the underlying subscriptions. */
  composition_warnings: CompositionWarning[]
  /** True when the current deal has at least one product activating
   *  later than the deal's start date. Informational on Flow B — the
   *  service-change diff doesn't currently split a service-line Increase
   *  across multiple effective_dates per flighted product. */
  is_flighted: boolean
  /** Per-product future-activation entries (informational). */
  flighted_activations: Array<{
    /** Products live on the line at the new level, comma-joined. */
    product_name: string
    service_line: string
    change_type: 'Increase' | 'Decrease' | 'Cancel'
    /** Line MRR the day before the step. */
    current_mrr: number
    /** Line MRR from the effective date onward. */
    recurring_amount: number
    effective_date: string
    length_of_change_months: number
    description: string
  }>
}

export interface CompositionWarning {
  service_line: string
  unchanged_mrr: number
  dropped: Array<{ product_name: string; mrr: number }>
  added: Array<{ product_name: string; mrr: number }>
  repriced: Array<{ product_name: string; old_mrr: number; new_mrr: number }>
}

export interface FlowIoSubmissionPreview {
  service_line: string
  product_name: string
  current_mrr: number
  new_mrr: number
  delta_mrr: number
  effective_date: string
  length_of_change_months: number
  ongoing_request: 'No'
  description: string
}

export interface FlowIoPreview {
  deal_type: string | null
  submissions: FlowIoSubmissionPreview[]
  baseline_missing: boolean
}

export type SubscriptionResolutionStatus = 'kept' | 'will_cancel' | 'other_chain' | 'no_predecessor'

export interface SubscriptionResolution {
  subscription_id: number
  status: SubscriptionResolutionStatus
  /** `service_line_present` = couldn't precisely tie the live sub to a
   *  new-deal product, but the new deal has products on the same
   *  service line — Heartbeat will update the sub in place rather than
   *  cancel it. Common for major versions where new PROD_ ids are
   *  generated and Heartbeat sub names differ from Grobotic product names. */
  match_method: 'external_id' | 'service_line+name' | 'service_line_present' | null
  match_reason: string
}

export interface RenewalContext {
  predecessor: {
    PK: string
    SK: string
    name: string | null
    type: string | null
    semanticVersion: string | null
    endDate: string | null
  }
  reason: 'predecessor_expired' | 'no_hb_contract'
}

export interface DealHeartbeatPreview {
  flow: 'A' | 'B' | 'IO' | 'none'
  skipReason?: 'org_not_found' | 'deal_not_found'
  /** Products whose pricing would push a wrong MRR. Non-empty means the real
   *  push will refuse until the deal is corrected. */
  pricing_problems: Array<{
    serviceLine: string
    products: Array<{ productName: string; sk?: string; price: number; margin: number }>
    pricedTotal: number
    reason: 'service_line_zero_mrr'
    detail: string
  }>
  org: {
    PK: string
    name: string | null
    heartbeat_account_id: number | null
    contractedBy: string[] | null
  } | null
  account: AccountPreview | null
  parent_account: ParentAccountPreview
  submitter: SubmitterPreview
  flow_a?: FlowAContractPreview
  flow_b?: FlowBPreview
  flow_io?: FlowIoPreview
  /** Set when the resolved predecessor's contract period closed before
   *  this deal's start_date — the push routes through Flow A (new
   *  contract) instead of Flow B (no MRR diff to emit). */
  renewal_context?: RenewalContext
  current_heartbeat_state?:
    | { available: true; snapshot: HeartbeatSnapshot; subscription_resolutions: SubscriptionResolution[] }
    | { available: false; reason: 'no_account_id' | 'tunnel_unavailable' | 'fetch_failed' }
}

export interface DealHeartbeatPreviewResponse {
  success: true
  preview: DealHeartbeatPreview
}

export interface DealHeartbeatPreviewError {
  success: false
  code: string
  error: string
}

export const fetchDealHeartbeatPreview = async (
  PK: string,
  SK: string,
): Promise<DealHeartbeatPreviewResponse> => {
  const response = await axios.get<DealHeartbeatPreviewResponse>(
    `${baseUrl}/aws/heartbeat/deal/${encodeURIComponent(PK)}/${encodeURIComponent(SK)}/preview`,
  )
  return response.data
}

export const useDealHeartbeatPreview = (PK: string | undefined, SK: string | undefined) => {
  return useQuery<DealHeartbeatPreviewResponse, AxiosError<DealHeartbeatPreviewError>>({
    queryKey: ['deal-heartbeat-preview', PK, SK],
    queryFn: (): Promise<DealHeartbeatPreviewResponse> => fetchDealHeartbeatPreview(PK as string, SK as string),
    enabled: Boolean(PK && SK),
    staleTime: 30_000,
    retry: false,
  })
}
